import { projectOrbit, timing } from './homepage-hero-galaxy-model.mjs';
import type { Orbit } from './homepage-hero-galaxy-model.mjs';

type Diamond = Orbit & {
  path: SVGPathElement;
};

class HomepageHeroGalaxy extends HTMLElement {
  private diamonds: Diamond[] = [];

  private toggle: HTMLButtonElement | null = null;

  private motion: MediaQueryList | null = null;

  private observer: IntersectionObserver | null = null;

  private animation = 0;

  private initialization = 0;

  private elapsed = 0;

  private previousTime: number | null = null;

  private previousFrame = -1;

  private visible = false;

  private userPaused = false;

  private reducedMotionOverride = false;

  private get paused() {
    return this.userPaused || (!!this.motion?.matches && !this.reducedMotionOverride);
  }

  connectedCallback() {
    // ACF and Storybook can insert the element before its children are attached.
    this.initialization = requestAnimationFrame(() => this.initialize());
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.initialization);
    this.stop();
    this.visible = false;
    this.observer?.disconnect();
    this.motion?.removeEventListener('change', this.onMotionChange);
    this.ownerDocument.removeEventListener('visibilitychange', this.syncPlayback);
    this.toggle?.removeEventListener('click', this.onToggle);
  }

  private initialize() {
    this.diamonds = Array.from(this.querySelectorAll<SVGPathElement>('[data-orbit]')).map((path) => {
      const [phaseRadians, centerX, centerY] = (path.dataset.orbit ?? '').split(',').map(Number);
      return { path, phaseRadians, centerX, centerY };
    }).filter(({ phaseRadians, centerX, centerY }) => [phaseRadians, centerX, centerY].every(Number.isFinite));
    if (!this.diamonds.length) return;

    this.toggle = this.querySelector<HTMLButtonElement>('button');
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.motion.addEventListener('change', this.onMotionChange);
    this.ownerDocument.addEventListener('visibilitychange', this.syncPlayback);
    this.toggle?.addEventListener('click', this.onToggle);
    if (this.toggle) this.toggle.hidden = false;

    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.syncPlayback();
    });
    this.observer.observe(this.querySelector('svg') ?? this);
    this.previousFrame = Math.floor(this.elapsed * timing.frameRate);
    this.renderFrame(this.previousFrame / timing.frameRate);
    this.syncPlayback();
  }

  private onMotionChange = () => {
    // Honor a new OS preference without discarding an explicit user pause.
    this.reducedMotionOverride = false;
    this.syncPlayback();
  };

  private onToggle = () => {
    const shouldPlay = this.paused;
    this.userPaused = !shouldPlay;
    // Play can opt into motion even when the OS currently requests less motion.
    this.reducedMotionOverride = shouldPlay && (this.motion?.matches ?? false);
    this.syncPlayback();
  };

  private syncPlayback = () => {
    if (this.toggle) {
      this.toggle.dataset.state = this.paused ? 'paused' : 'playing';
      const label = this.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label', label ?? (this.paused ? 'Play background animation' : 'Pause background animation'));
    }
    if (this.paused || !this.visible || this.ownerDocument.hidden || !this.isConnected) {
      this.stop();
    } else if (!this.animation) {
      this.animation = requestAnimationFrame(this.tick);
    }
  };

  private stop() {
    cancelAnimationFrame(this.animation);
    this.animation = 0;
    this.previousTime = null;
  }

  private tick = (timestamp: number) => {
    if (this.previousTime !== null) {
      this.elapsed = (this.elapsed + (timestamp - this.previousTime) / 1000) % timing.loopSeconds;
    }
    this.previousTime = timestamp;
    const frame = Math.floor(this.elapsed * timing.frameRate);
    if (frame !== this.previousFrame) {
      this.renderFrame(frame / timing.frameRate);
      this.previousFrame = frame;
    }
    this.animation = requestAnimationFrame(this.tick);
  };

  private renderFrame(seconds: number) {
    this.diamonds.forEach((diamond) => {
      diamond.path.setAttribute('transform', projectOrbit(diamond, seconds));
    });
  }
}

if (!customElements.get('skill-hero-galaxy')) {
  customElements.define('skill-hero-galaxy', HomepageHeroGalaxy);
}
